import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { FiSearch, FiSliders, FiX } from "react-icons/fi";
import Pagination from "../Components/Pagination";
import Card from "../Components/Card";
import properties, { locations, propertyTypes, priceRanges } from "../data/properties";

const ITEMS_PER_PAGE = 8;

export default function Houses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  const [type, setType] = useState("");
  const [priceRange, setPriceRange] = useState(0);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, location, type, priceRange, sortBy]);

  const filtered = useMemo(() => {
    let result = [...properties];

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.location.toLowerCase().includes(q) ||
          p.type.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      );
    }

    // Location
    if (location && location !== "All Locations") {
      result = result.filter((p) => p.location === location);
    }

    // Type
    if (type && type !== "All Types") {
      result = result.filter((p) => p.type === type);
    }

    // Price
    const range = priceRanges[priceRange];
    if (range) {
      result = result.filter((p) => p.price >= range.min && p.price <= range.max);
    }

    // Sort
    switch (sortBy) {
      case "price-low":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        result.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        result.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
        break;
      case "oldest":
        result.sort((a, b) => new Date(a.dateAdded) - new Date(b.dateAdded));
        break;
      case "area":
        result.sort((a, b) => b.area - a.area);
        break;
      default:
        break;
    }

    return result;
  }, [search, location, type, priceRange, sortBy]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const resetFilters = () => {
    setSearch("");
    setLocation("");
    setType("");
    setPriceRange(0);
    setSortBy("newest");
    setSearchParams({});
  };

  const hasActiveFilters = search || location || type || priceRange > 0;

  return (
    <div className="page-wrapper" style={{ marginTop: "62px" }}>
      {/* Page Header */}
      <div className="page-header" style={{ padding: "3rem 0 2rem" }}>
        <div className="container position-relative">
          <h1 className="mb-1" style={{ fontSize: "2rem" }}>Properties</h1>
          <p className="mb-0">Showing {filtered.length} properties available</p>
        </div>
      </div>

      <div className="container py-4">
        {/* Filter Bar */}
        <div className="filter-bar fade-in-up">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill d-flex align-items-center gap-1"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FiSliders size={14} />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              {hasActiveFilters && (
                <button
                  className="btn btn-sm btn-outline-danger rounded-pill d-flex align-items-center gap-1"
                  onClick={resetFilters}
                >
                  <FiX size={14} /> Clear All
                </button>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="text-muted small mb-0">Sort by:</label>
              <select
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ width: "auto", borderRadius: 8 }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="area">Largest Area</option>
              </select>
            </div>
          </div>

          {showFilters && (
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FiSearch className="text-muted" />
                  </span>
                  <input
                    className="form-control"
                    type="search"
                    placeholder="Search by name, location, type..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                >
                  {locations.map((loc) => (
                    <option key={loc} value={loc === "All Locations" ? "" : loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  {propertyTypes.map((t) => (
                    <option key={t} value={t === "All Types" ? "" : t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select"
                  value={priceRange}
                  onChange={(e) => setPriceRange(Number(e.target.value))}
                >
                  {priceRanges.map((r, i) => (
                    <option key={i} value={i}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <button className="btn btn-filter w-100" onClick={() => setCurrentPage(1)}>
                  <FiSearch className="me-1" /> Search
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {paginated.length > 0 ? (
          <>
            <div className="row g-4">
              {paginated.map((property) => (
                <div className="col-sm-6 col-lg-3" key={property.id}>
                  <Card property={property} />
                </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🏠</div>
            <h3>No Properties Found</h3>
            <p>Try adjusting your filters or search terms to find what you're looking for.</p>
            <button className="btn btn-primary rounded-pill px-4" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
